import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { apiClient } from '../../packages/api-client';
import { adminListTickets } from '../../packages/api-client/support-api';
import { usePageTitle } from '../../hooks/usePageTitle';
import { PageHeader, StateView } from '../../packages/ui';
import './platform.css';

type Tenant = { id: string; name: string; is_active: boolean; created_at: string; deleted_at?: string | null };
/** Fields used from adminbilling.TenantRow (GET /admin/billing/tenants, non-deleted tenants only). */
type BillingRow = { id: string; user_count: number; project_count: number; ai_calls: number; ai_tokens: number };
type Tickets = { open: number; inProgress: number };

type Source<T> = { state: 'loading' } | { state: 'error' } | { state: 'ok'; data: T; at: Date };

const DAY = 86_400_000;
const GRACE_DAYS = 30; // tenants.GraceDays

function useSource<T>(fetcher: () => Promise<T>): [Source<T>, () => void] {
    const [src, setSrc] = useState<Source<T>>({ state: 'loading' });
    const load = useCallback(() => {
        setSrc({ state: 'loading' });
        fetcher().then((data) => setSrc({ state: 'ok', data, at: new Date() })).catch(() => setSrc({ state: 'error' }));
    }, [fetcher]);
    useEffect(() => { load(); }, [load]);
    return [src, load];
}

const fetchTenants = () => apiClient.get<{ data: Tenant[] }>('/tenants').then((r) => r.data.data ?? []);
const fetchBilling = () => apiClient.get<BillingRow[]>('/admin/billing/tenants', { params: { window: '30d' } }).then((r) => r.data ?? []);
const fetchTickets = () => Promise.all([adminListTickets({ status: 'open' }), adminListTickets({ status: 'in_progress' })])
    .then(([o, p]) => ({ open: o.total, inProgress: p.total }));

interface Metric { label: string; value: number; meaning: string }

function Metrics({ items }: { items: Metric[] }) {
    return (
        <dl className="stats-grid platform-metrics">
            {items.map((m) => (
                <div key={m.label} className="stat-card" data-metric>
                    <dt className="stat-label">{m.label}</dt>
                    <dd className="stat-value" data-testid="value">{m.value.toLocaleString()}</dd>
                    <dd className="platform-metric-def">{m.meaning}</dd>
                </div>
            ))}
        </dl>
    );
}

/** One source's block: its metrics with freshness, or a truthful loading/unavailable state in their place. */
function SourceSection<T>({ title, source, what, link, onRetry, children }: {
    title: string; source: Source<T>; what: string; link: React.ReactNode; onRetry: () => void;
    children: (data: T) => React.ReactNode;
}) {
    return (
        <section className="platform-section" aria-label={title}>
            <div className="platform-section-head">
                <h2>{title}</h2>
                {link}
            </div>
            {source.state === 'loading' && <StateView state="loading" label={`Loading ${title.toLowerCase()}`} />}
            {source.state === 'error' && (
                <StateView state="error" title={`${what} unavailable`}
                    message="The request failed, so these figures are omitted rather than shown as zero." onRetry={onRetry} />
            )}
            {source.state === 'ok' && (
                <>
                    {children(source.data)}
                    <p className="platform-fresh">Fetched {source.at.toLocaleTimeString()}. Figures do not update live; use Refresh.</p>
                </>
            )}
        </section>
    );
}

/**
 * Platform › Overview: totals from existing superadmin endpoints only. Each
 * figure states what it counts; a source that fails is omitted and says so.
 * There is no health endpoint, so no service-health status is shown.
 */
export function PlatformOverviewPage() {
    usePageTitle('Platform overview');
    const [tenants, reloadTenants] = useSource(fetchTenants);
    const [billing, reloadBilling] = useSource(fetchBilling);
    const [tickets, reloadTickets] = useSource(fetchTickets);
    const refresh = () => { reloadTenants(); reloadBilling(); reloadTickets(); };

    return (
        <div>
            <PageHeader
                breadcrumb={[{ label: 'Platform console' }, { label: 'Overview' }]}
                title="Platform overview"
                subtitle="Totals across every tenant, read from the tenant registry, billing and support endpoints when this page loaded."
                actions={<button type="button" className="btn btn-secondary btn-md" onClick={refresh}><RefreshCw size={16} aria-hidden="true" /> Refresh</button>}
            />

            <SourceSection title="Workspaces" what="Tenant registry is" source={tenants} onRetry={reloadTenants}
                link={<Link to="/admin/tenants">Manage tenants →</Link>}>
                {(list) => {
                    const live = list.filter((t) => !t.deleted_at);
                    const scheduled = list.filter((t) => t.deleted_at);
                    const since = Date.now() - 30 * DAY;
                    return (
                        <>
                            <Metrics items={[
                                { label: 'Active workspaces', value: live.filter((t) => t.is_active).length, meaning: 'Tenants marked active and not scheduled for deletion.' },
                                { label: 'Inactive workspaces', value: live.filter((t) => !t.is_active).length, meaning: 'Tenants an operator marked inactive, not scheduled for deletion.' },
                                { label: 'Scheduled for deletion', value: scheduled.length, meaning: `In the ${GRACE_DAYS}-day recovery window; restorable until purged.` },
                                { label: 'Created in the last 30 days', value: list.filter((t) => new Date(t.created_at).getTime() >= since).length, meaning: 'By creation date, including any since scheduled for deletion.' },
                            ]} />
                            {scheduled.length > 0 && (
                                <>
                                    <h3 className="stat-label">Scheduled for deletion</h3>
                                    <ul className="platform-list">
                                        {scheduled.map((t) => {
                                            const purge = new Date(new Date(t.deleted_at as string).getTime() + GRACE_DAYS * DAY);
                                            return <li key={t.id}><Link to={`/admin/tenants/${t.id}`}>{t.name}</Link> — purged on {purge.toLocaleDateString()} unless restored</li>;
                                        })}
                                    </ul>
                                </>
                            )}
                        </>
                    );
                }}
            </SourceSection>

            <SourceSection title="People & usage" what="Usage totals are" source={billing} onRetry={reloadBilling}
                link={<Link to="/admin/billing/tenants">Billing &amp; AI usage →</Link>}>
                {(rows) => {
                    const sum = (k: keyof Omit<BillingRow, 'id'>) => rows.reduce((n, r) => n + (r[k] ?? 0), 0);
                    return (
                        <Metrics items={[
                            { label: 'People', value: sum('user_count'), meaning: 'User accounts (not deleted) in tenants not scheduled for deletion.' },
                            { label: 'Projects', value: sum('project_count'), meaning: 'Projects (not deleted) in tenants not scheduled for deletion.' },
                            { label: 'AI calls, last 30 days', value: sum('ai_calls'), meaning: 'Recorded AI requests across those tenants in the last 30 days.' },
                            { label: 'AI tokens, last 30 days', value: sum('ai_tokens'), meaning: 'Input plus output tokens for those calls.' },
                        ]} />
                    );
                }}
            </SourceSection>

            <SourceSection title="Support" what="Support ticket counts are" source={tickets} onRetry={reloadTickets}
                link={<Link to="/admin/support">Open support inbox →</Link>}>
                {(t) => (
                    <Metrics items={[
                        { label: 'Open support tickets', value: t.open, meaning: 'Tickets in the "open" status, across all tenants.' },
                        { label: 'Tickets in progress', value: t.inProgress, meaning: 'Tickets in the "in progress" status, across all tenants.' },
                    ]} />
                )}
            </SourceSection>
        </div>
    );
}
